import React from "react";
import ATMSelect from "../UI/atoms/formFields/ATMSelect/ATMSelect";
import ATMTextField from "../UI/atoms/formFields/ATMTextField/ATMTextField";
import { useGetAoCodeListQuery } from "src/services/AOCodeService";

// The Assessing Officer code, captured at login in place of the old Remark
// field. It is carried on the application and pre-fills the four Part D cells
// of the PAN form, so it is captured in the same four parts the form prints:
// Area Code (3), AO Type (2), Range Code (3), AO Number (2).
//
// Picking a city from the AO Code master fills all four at once. The inputs stay
// editable underneath for a code the master does not carry yet — the master is
// maintained by hand (Configuration > AO Code List) and is never complete.

export type AOCodeValue = {
  aoAreaCode: string;
  aoType: string;
  aoRangeCode: string;
  aoNo: string;
};

type Props = {
  values: AOCodeValue;
  onChange: (patch: Partial<AOCodeValue>) => void;
};

const paramList = [
  "_id",
  "city",
  "areaCode",
  "aoType",
  "rangeCode",
  "aoNo",
  "isDeleted",
  "isActive",
  "createdAt",
  "updatedAt",
];

/** "DLC / W / 123 / 4" — how the code reads on the form and in the master. */
const codeOf = (row: any) =>
  [row?.areaCode, row?.aoType, row?.rangeCode, row?.aoNo]
    .map((part) => (part === null || part === undefined ? "" : `${part}`))
    .join(" / ");

const AOCodeField = ({ values, onChange }: Props) => {
  const { data, isLoading, isFetching } = useGetAoCodeListQuery({
    limit: 0,
    searchValue: "",
    params: paramList,
    page: 0,
    filterBy: [
      {
        fieldName: "",
        value: [],
      },
    ],
    dateFilter: {
      start_date: "",
      end_date: "",
      dateFilterKey: "",
    },
    orderBy: "city",
    orderByValue: 1,
    isPaginationRequired: false,
  });

  const rows: any[] = React.useMemo(() => data?.data || [], [data]);

  const options = React.useMemo(
    () =>
      rows.map((row: any) => ({
        label: `${row?.city} — ${codeOf(row)}`,
        value: row?._id,
      })),
    [rows]
  );

  /*
    The picker follows the four inputs rather than holding a selection of its
    own: an operator who types a code by hand should not be left looking at a
    stale city, and re-opening the dialog on a saved code should show the city
    it came from.
  */
  const selectedId =
    rows.find(
      (row: any) =>
        `${row?.areaCode || ""}`.toUpperCase() ===
          (values.aoAreaCode || "").toUpperCase() &&
        `${row?.aoType || ""}`.toUpperCase() ===
          (values.aoType || "").toUpperCase() &&
        `${row?.rangeCode ?? ""}` === (values.aoRangeCode || "") &&
        `${row?.aoNo ?? ""}` === (values.aoNo || "")
    )?._id || "";

  const handlePick = (id: any) => {
    const row = rows.find((item: any) => item?._id === id);
    if (!row) return;
    onChange({
      aoAreaCode: `${row.areaCode || ""}`.toUpperCase(),
      aoType: `${row.aoType || ""}`.toUpperCase(),
      aoRangeCode: `${row.rangeCode ?? ""}`,
      aoNo: `${row.aoNo ?? ""}`,
    });
  };

  const part = (
    key: keyof AOCodeValue,
    label: string,
    maxLength: number,
    digitsOnly?: boolean
  ) => (
    <ATMTextField
      name={key}
      value={values[key]}
      label={label}
      required
      placeholder={label}
      onChange={(e) => {
        const raw = e.target.value;
        onChange({
          [key]: digitsOnly
            ? raw.replace(/\D/g, "").slice(0, maxLength)
            : raw.toUpperCase().slice(0, maxLength),
        } as Partial<AOCodeValue>);
      }}
    />
  );

  return (
    <div className="flex flex-col gap-2">
      <ATMSelect
        name="aoCodeCity"
        label="AO Code"
        required
        value={selectedId}
        options={options}
        isSearch
        isLoading={isLoading || isFetching}
        onChange={handlePick}
      />
      <div className="text-[11px] text-slate-500">
        Pick the city to fill the code, or type all four parts below. All four
        are required.
      </div>
      <div className="grid grid-cols-4 gap-2 pb-5">
        {part("aoAreaCode", "Area Code", 3)}
        {part("aoType", "AO Type", 2)}
        {part("aoRangeCode", "Range Code", 3, true)}
        {part("aoNo", "AO No.", 2, true)}
      </div>
    </div>
  );
};

export default AOCodeField;
